/**
 * Utilitários centralizados de formatação
 * Consolidação de todas as funções de formatação da aplicação
 */

// ============= FORMATAÇÃO DE NÚMEROS =============

/**
 * Remove todos os caracteres não numéricos
 */
export function keepOnlyDigits(str: string | number | null | undefined): string {
  if (str == null) return '';
  return String(str).replace(/\D/g, '');
}

// ============= FORMATAÇÃO DE DOCUMENTOS =============

/**
 * Formata CNPJ no padrão XX.XXX.XXX/XXXX-XX
 */
export function formatarCNPJ(cnpj: string | null | undefined): string {
  if (!cnpj) return '';
  const cnpjOnly = keepOnlyDigits(cnpj);
  if (cnpjOnly.length !== 14) return cnpj;
  return cnpjOnly.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

/**
 * Normaliza CNPJ removendo formatação e preenchendo com zeros à esquerda
 */
export function normalizarCNPJ(cnpj: string | null | undefined): string {
  return keepOnlyDigits(cnpj).padStart(14, '0');
}

/**
 * Formata CPF no padrão XXX.XXX.XXX-XX
 */
export function formatarCPF(cpf: string | null | undefined): string {
  if (!cpf) return '';
  const cleaned = keepOnlyDigits(cpf);
  if (cleaned.length !== 11) return cpf;
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Formata CPF parcialmente ocultando dígitos (para exibição)
 */
export function formatarCPFParcial(cpf: string | null | undefined): string {
  if (!cpf) return '';
  const cpfOnly = keepOnlyDigits(cpf);
  if (cpfOnly.length !== 11) return cpf;
  return `***${cpfOnly.slice(3, 9)}**`;
}

/**
 * Formata CEP no padrão XXXXX-XXX
 */
export function formatarCEP(cep: string | null | undefined): string {
  if (!cep) return '';
  const cepOnly = keepOnlyDigits(cep);
  if (cepOnly.length !== 8) return cep;
  return cepOnly.replace(/^(\d{5})(\d{3})$/, '$1-$2');
}

/**
 * Normaliza CEP removendo formatação e preenchendo com zeros
 */
export function normalizarCEP(cep: string | null | undefined): string {
  return keepOnlyDigits(cep).padStart(8, '0');
}

// ============= FORMATAÇÃO DE TELEFONE =============

/**
 * Verifica se um número é celular baseado no primeiro dígito após DDD
 * Celulares brasileiros começam com 9, 8, 7 ou 6
 */
function isCelular(digitsWithDDD: string): boolean {
  if (digitsWithDDD.length < 10) return false;
  const firstDigitAfterDDD = digitsWithDDD.charAt(2);
  return ['9', '8', '7', '6'].includes(firstDigitAfterDDD);
}

/**
 * Formata telefone completo com DDD no padrão (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
 * Automaticamente adiciona o 9º dígito em celulares brasileiros incompletos
 */
export function formatarTelefone(ddd: string, telefone: string): string;
export function formatarTelefone(telefoneCompleto: string): string;
export function formatarTelefone(dddOrPhone: string, telefone?: string): string {
  // Sobrecarga: Se telefone for undefined, assume que é número completo
  if (!telefone) {
    let phoneOnly = keepOnlyDigits(dddOrPhone);
    if (!phoneOnly) return '';
    
    // Remover código do país '55' se presente
    if (phoneOnly.startsWith('55') && phoneOnly.length >= 12) {
      phoneOnly = phoneOnly.slice(2);
    }
    
    // Adicionar 9 se for celular com apenas 10 dígitos (DDD + 8 dígitos)
    if (phoneOnly.length === 10 && isCelular(phoneOnly)) {
      const ddd = phoneOnly.slice(0, 2);
      const numero = phoneOnly.slice(2);
      phoneOnly = `${ddd}9${numero}`; // Ex: 5591382207 → 55991382207
    }
    
    // Agora extrai DDD corretamente (sem o 55)
    if (phoneOnly.length >= 10) {
      const ddd = phoneOnly.slice(0, 2);
      const numero = phoneOnly.slice(2);
      return formatarTelefone(ddd, numero);
    }
    return dddOrPhone;
  }

  // Formato padrão com DDD e telefone separados
  if (!dddOrPhone || !telefone) return '';
  let tel = keepOnlyDigits(telefone);
  
  // Remover zeros iniciais do número (ex: "03462-7080" → "3462-7080")
  tel = tel.replace(/^0+/, '');
  
  // Adicionar 9 se for celular com apenas 8 dígitos
  if (tel.length === 8) {
    const firstDigit = tel.charAt(0);
    if (['9', '8', '7', '6'].includes(firstDigit)) {
      tel = `9${tel}`; // Ex: 91382207 → 991382207
    }
  }
  
  if (tel.length === 9) {
    return `(${dddOrPhone}) ${tel.slice(0, 5)}-${tel.slice(5)}`;
  } else if (tel.length === 8) {
    return `(${dddOrPhone}) ${tel.slice(0, 4)}-${tel.slice(4)}`;
  }
  return `(${dddOrPhone}) ${tel}`;
}

/**
 * Normaliza telefone para formato internacional (+55XXXXXXXXXXX)
 */
export function normalizarTelefone(ddd?: string, telefone?: string): string {
  if (!ddd || !telefone) return '';
  return `+55${ddd}${keepOnlyDigits(telefone)}`;
}

/**
 * Formata telefone para cópia (remove +55 se presente)
 */
export function formatPhoneForCopy(phone: string | null | undefined): string {
  const digits = keepOnlyDigits(phone);
  return digits.startsWith('55') && (digits.length === 12 || digits.length === 13) ? digits.slice(2) : digits;
}

// ============= FORMATAÇÃO DE VALORES =============

/**
 * Formata valores monetários em formato brasileiro
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value == null) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

/**
 * Alias para formatCurrency (padrão PT-BR)
 */
export const formatarMoeda = formatCurrency;

/**
 * Formata número para exibição monetária no input
 */
export function formatCurrencyInput(value: number | null | undefined): string {
  if (!value) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

/**
 * Parse de string monetária para número
 */
export function parseCurrencyInput(text: string): number {
  if (!text) return 0;
  
  // Remove símbolos e espaços
  let cleaned = text.replace(/[R$\s]/g, '');
  
  // Se tem vírgula mas não tem ponto, considera vírgula como decimal
  if (cleaned.includes(',') && !cleaned.includes('.')) {
    cleaned = cleaned.replace(',', '.');
  } else if (cleaned.includes('.') && cleaned.includes(',')) {
    // Se tem ambos, remove ponto (milhar) e troca vírgula por ponto
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  }
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

// ============= FORMATAÇÃO DE DATAS =============

/**
 * Formata data para exibição em português (DD/MM/AAAA)
 */
export function formatarData(data: string | Date | null | undefined): string {
  if (!data) return '';
  try {
    const date = typeof data === 'string' ? new Date(data) : data;
    return date.toLocaleDateString('pt-BR');
  } catch {
    return String(data);
  }
}

/**
 * Formata data e hora para exibição em português
 */
export function formatarDataHora(data: string | Date | null | undefined): string {
  if (!data) return '';
  try {
    const date = typeof data === 'string' ? new Date(data) : data;
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return String(data);
  }
}

/**
 * Formata data compacta (DD/MM)
 */
export function formatarDataCompacta(data: string | Date | null | undefined): string {
  if (!data) return '';
  try {
    const date = typeof data === 'string' ? new Date(data) : data;
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit'
    });
  } catch {
    return String(data);
  }
}

/**
 * Formata tempo relativo de forma compacta
 */
export function formatarTempoRelativo(data: string): string {
  const agora = new Date();
  const dataPassada = new Date(data);
  const diffMs = agora.getTime() - dataPassada.getTime();
  
  const minutos = Math.floor(diffMs / 60000);
  const horas = Math.floor(diffMs / 3600000);
  const dias = Math.floor(diffMs / 86400000);
  const semanas = Math.floor(diffMs / 604800000);
  
  if (minutos < 1) {
    return 'agora';
  } else if (minutos < 60) {
    return `${minutos}m`;
  } else if (horas < 24) {
    return `${horas}h`;
  } else if (dias < 7) {
    return `${dias}d`;
  } else {
    return `${semanas}s`;
  }
}

// ============= FORMATAÇÃO DE TEXTO =============

/**
 * Formata textos de enums para exibição padronizada
 */
export function formatEnumText(text: string | null | undefined): string {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Formata tipos de família de produto
 */
export function formatFamilia(familia: string): string {
  const familiaMap: Record<string, string> = {
    'Móvel': 'Móvel',
    'Fibra': 'Fibra',
    'Fixa Básica': 'Fixa Básica',
    'Fixa Avançada': 'Fixa Avançada',
    'TI': 'TI',
    'Aparelhos': 'Aparelhos',
    'Energia': 'Energia',
    'Totalização': 'Totalização',
    'Outro': 'Outro'
  };
  
  return familiaMap[familia] || formatEnumText(familia);
}

/**
 * Formata tipos de faturamento
 */
export function formatFaturamentoTipo(tipo: string): string {
  const tipoMap: Record<string, string> = {
    'VENDA NOVA': 'Venda Nova',
    'MIGRAÇÃO': 'Migração',
    'APARELHO': 'Aparelho',
    'ENERGIA': 'Energia',
    'NÃO PAGO': 'Não Pago',
    'OUTRO': 'Outro'
  };
  
  return tipoMap[tipo] || formatEnumText(tipo);
}

// ============= FORMATAÇÃO DE PEDIDOS =============

/**
 * Formata o código do pedido no padrão #P{numero}
 */
export function formatPedidoCode(numeroPedido: number): string {
  const numeroInteiro = Math.floor(Number(numeroPedido));
  
  if (!Number.isFinite(numeroInteiro) || numeroInteiro <= 0) {
    console.error('⚠️ numeroPedido inválido:', numeroPedido);
    return '#P?';
  }
  
  return `#P${numeroInteiro}`;
}

/**
 * Formata o código do item no padrão #P{numero}-{seq}
 */
export function formatItemCode(numeroPedido: number, seq: number): string {
  const numeroInteiro = Math.floor(numeroPedido);
  return `#P${numeroInteiro}-${seq}`;
}

/**
 * Converte numero_pedido de notação científica para inteiro
 */
export function parseNumeroPedido(numero: any): number {
  if (!numero) return 0;
  return Math.floor(Number(numero));
}

/**
 * Parse detalhado do número do pedido
 */
export function parsePedidoCodeDetailed(numeroPedido: number): {
  formato: 'antigo' | 'novo';
  ano?: number;
  mes?: number;
  dia?: number;
  sequencial: number;
} {
  const str = numeroPedido.toString();
  
  if (str.length === 10) {
    return {
      formato: 'novo',
      ano: parseInt(str.substring(0, 2), 10),
      mes: parseInt(str.substring(2, 4), 10),
      dia: parseInt(str.substring(4, 6), 10),
      sequencial: parseInt(str.substring(6, 10), 10)
    };
  } else if (str.length === 7) {
    return {
      formato: 'antigo',
      ano: parseInt(str.substring(0, 2), 10),
      sequencial: parseInt(str.substring(2, 7), 10)
    };
  } else {
    return {
      formato: 'antigo',
      sequencial: numeroPedido
    };
  }
}

// ============= FORMATAÇÃO DE ENDEREÇOS =============

/**
 * Normaliza telefone que pode estar em formato científico (ex: 5,55198E+12)
 * Retorna string numérica limpa ou null se inválido
 */
export function normalizarTelefoneStaging(tel: string | null | undefined): string | null {
  if (!tel) return null;
  const telStr = String(tel).trim();
  if (!telStr) return null;
  
  // Notação científica (E+) = o número perdeu precisão na origem (Excel converteu p/ float).
  // Reconstruir geraria um telefone com os últimos dígitos zerados (errado/clicável) — descartar.
  if (/[eE]\+/.test(telStr)) {
    return null;
  }
  
  // Limpar não-dígitos
  const digits = telStr.replace(/\D/g, '');
  // Remover código do país 55 se presente
  const semPais = digits.startsWith('55') && digits.length >= 12 ? digits.slice(2) : digits;
  return semPais.length >= 10 ? semPais : null;
}

/**
 * Formata endereço em uma única linha
 */
export function formatarEnderecoLinhaUnica(data: {
  endereco_rua?: string;
  endereco_numero?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_uf?: string;
  endereco_cep?: string;
} | {
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
  cep?: string;
}): string {
  const partes: string[] = [];
  
  // Suporta duas nomenclaturas diferentes
  const rua = 'endereco_rua' in data ? data.endereco_rua : ('logradouro' in data ? data.logradouro : undefined);
  const numero = 'endereco_numero' in data ? data.endereco_numero : ('numero' in data ? data.numero : undefined);
  const bairro = 'endereco_bairro' in data ? data.endereco_bairro : ('bairro' in data ? data.bairro : undefined);
  const cidade = 'endereco_cidade' in data ? data.endereco_cidade : ('cidade' in data ? data.cidade : undefined);
  const uf = 'endereco_uf' in data ? data.endereco_uf : ('uf' in data ? data.uf : undefined);
  const cep = 'endereco_cep' in data ? data.endereco_cep : ('cep' in data ? data.cep : undefined);
  
  if (rua) partes.push(rua);
  if (numero) partes.push(numero);
  if (bairro) partes.push(bairro);
  if (cidade && uf) {
    partes.push(`${cidade}/${uf}`);
  } else if (cidade) {
    partes.push(cidade);
  } else if (uf) {
    partes.push(uf);
  }
  if (cep) partes.push(`CEP ${formatarCEP(cep)}`);
  
  return partes.join(', ');
}
